import "react-datepicker/dist/react-datepicker.css";
import { useCallback,useMemo,useRef, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
    ColDef,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    LoadSuccessParams,
    GridApi,
    SizeColumnsToContentStrategy,
    ValueFormatterParams,
    ICellRendererParams
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";
import useReference from "src/hooks/useReference";
import { CrossBinTransferSummaryModel } from "src/models/CrossBinTransferSummaryModel";
import { CrossBinTransferDetailModel } from "../../../models/CrossBinTransferDetailModel";

const CrossBinTransferSummary = () => {
    const { setIsOpen } = useSidebar();
    const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

    const [gridReady, setGridReady] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>();
    const [dataSource, setDataSource] = useState<IServerSideDatasource>();

    const [dataSourceDetails, setDataSourceDetails] = useState<IServerSideDatasource>();
    const [detailsGridApi, setDetailsGridApi] = useState<GridApi>();
    const [detailCriteria, setDetailCriteria] = useState<DetailCriteria | null>(null);

    const [hasRunReport, setHasRunReport] = useState(false);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);


    const baseReportRequestRef = useRef<any>(null);

    type ButtonRendererProps =
        ICellRendererParams<CrossBinTransferSummaryModel> & {
            handleViewDetailsReport: (
                row: CrossBinTransferSummaryModel
            ) => void;

            handleDownloadDetailsReport: (
                row: CrossBinTransferSummaryModel
            ) => void;
    };

    type CurrencyRow = {
        currencyNumericCode?: string | number | null;
    };

    type DetailCriteria = {
        fromBin: string;
        toBin: string;
    };

    const { currencyList } = useReference();

    const numericToAlpha = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        (currencyList ?? []).forEach((c: any) => {
            if (c?.numericCode != null && c?.alphaCode) {
                map[String(c.numericCode)] = String(c.alphaCode).toUpperCase();
            }
        });
        return map;
    }, [currencyList]);

    const resolveAlphaFromRow = useCallback(
        (data?: CurrencyRow | null): string => {
            const feeVal = data?.currencyNumericCode;
            if (feeVal == null) return "USD";
            const str = String(feeVal).trim();
            if (/^[A-Za-z]{3}$/.test(str)) {
                return str.toUpperCase();
            }
            return numericToAlpha[str] ?? "USD";
        },
        [numericToAlpha]
    );

    const currencyFormatter = useCallback(
        (params: ValueFormatterParams<any, any>): string => {
            const value = params.value;
            if (value === null || value === undefined) return "";
            const amount = typeof value === "number"
                ? value
                : Number(value);
            if (Number.isNaN(amount)) return "";
            const alphaCode = resolveAlphaFromRow(params.data);
            return String(formatCurrency(amount, alphaCode));
        },
        [resolveAlphaFromRow]
    );


    const ViewDetailsButtonRenderer = (props: ButtonRendererProps) => {
        return (
            <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                    if (!props.data) {
                        return;
                    }
                    props.handleViewDetailsReport(props.data);
                }}
            >
                View Details
            </button>
        );
    };

    const handleViewDetailsReport = (row: CrossBinTransferSummaryModel) => {
        if (row.fromBin == null || row.toBin == null) {
            return;
        }

        const criteria: DetailCriteria = {
            fromBin: row.fromBin,
            toBin: row.toBin,
        };

        setDetailCriteria(criteria);
        setDataSourceDetails(createServerSideDatasourceDetails(criteria));
    };

    const DownloadDetailsButtonRenderer = (
        props: ButtonRendererProps
    ) => {
        return (
            <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                    if (!props.data) {
                        return;
                    }
                    props.handleDownloadDetailsReport(
                        props.data
                    );
                }}
            >
                Download Details
            </button>
        );
    };


    const handleDownloadDetailsReport = (
        row: CrossBinTransferSummaryModel) => {
        if (
            row.fromBin == null ||
            row.toBin == null
        ) {
            return;
        }
        setIsDownloadLoading(true);
        const request = {
            ...(baseReportRequestRef.current ??
                mapReportRequest(null)),

            fromBin: row.fromBin,
            toBin: row.toBin,
        };
        ReportApi.exportCrossBinTransferDetail(request)
            .then((res: any) => {
                downloadFile(
                    `CrossBinTransfer_${row.fromBin}_${row.toBin}`,
                    res.data
                );
            })
            .finally(() => {
                setIsDownloadLoading(false);
            });
    };

    const colDefs = useMemo<ColDef<CrossBinTransferSummaryModel>[]>(() => [
        { headerName: "From Bin", field: "fromBin" },
        { headerName: "To Bin", field: "toBin" },
        { headerName: "Total Transfer Amount", field: "totalTransferAmount", valueFormatter: currencyFormatter },
        { headerName: "", cellRenderer: ViewDetailsButtonRenderer, sortable: false, cellRendererParams: { handleViewDetailsReport } },
        { headerName: "", cellRenderer: DownloadDetailsButtonRenderer, sortable: false, cellRendererParams: { handleDownloadDetailsReport }}
    ], [currencyFormatter]);


    const colDefsDetails = useMemo<ColDef<CrossBinTransferDetailModel>[]>(() => [
        { headerName: "FromBin", field: "fromBin" },
        { headerName: "ToBin", field: "toBin" },
        { headerName: "Program", field: "program" },
        { headerName: "ActivatingMerchantGroup", field: "activatingMerchantGroup" },
        { headerName: "Date", field: "dateActivated" },
        { headerName: "FromCard", field: "fromCard" },
        { headerName: "ToCard", field: "toCard" },
        { headerName: "TransferAmount", field: "transferAmount", valueFormatter: currencyFormatter }
    ], [currencyFormatter]);


    const defaultColDef: ColDef = {
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
    };

    const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
        return {
            type: "fitCellContents",
            defaultMinWidth: 120,
            defaultMaxWidth: 300,
            skipHeader: false,
        };
    }, []);

    const handleViewReport = () => {

        //need  to caputure the original request to pass on to details 
        const baseRequest = mapReportRequest(null);
        baseReportRequestRef.current = baseRequest;

        // Clear existing details grid
        setDetailCriteria(null);
        setDataSourceDetails(undefined);

        setHasRunReport(true);
        setIsSummaryLoading(true)

        setGridReady(true);
        setDataSource(createServerSideDatasource());
    };

    const handleDownloadReport = () => {
        setIsDownloadLoading(true);
    
        ReportApi.exportCrossBinTransferSummary(mapReportRequest(null))
            .then((res: any) => {
                downloadFile(CrossBinTransferSummary.displayName!, res.data);
            })
            .finally(() => setIsDownloadLoading(false));
    };

    const createServerSideDatasource = (): IServerSideDatasource => {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                getReportData(params);
            },
        };
    };

    const createServerSideDatasourceDetails = (
        criteria: DetailCriteria
    ): IServerSideDatasource => {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                getDetailsReportData(params, criteria);
            },
        };
    };


    const getReportData = (params: IServerSideGetRowsParams | null) => {
        setIsOpen(false);

        ReportApi.getCrossBinTransferSummary(mapReportRequest(params))
            .then((res) => {
                const result = res.data;

                // Push page rows into grid
                const success: LoadSuccessParams = {
                    rowData: result.rows,
                    rowCount: result.totalCount,
                };
                if (result?.rows?.length > 0) {
                    params?.api?.hideOverlay();
                    params?.success(success);
                } else {
                    params?.api?.showNoRowsOverlay();
                    params?.success({ rowData: [], rowCount: 0 });
                }
            })
            .catch(() => params?.fail?.())
            .finally(() => {
                setIsSummaryLoading(false);
            });
    };

    const getDetailsReportData = (
        params: IServerSideGetRowsParams | null,
        criteria: DetailCriteria
    ) => {
        setIsOpen(false);

        const baseRequest = baseReportRequestRef.current ?? mapReportRequest(null);
        const gridRequest = mapReportRequest(params);


        const request = {
            ...baseRequest,
            startRow: gridRequest.startRow,
            endRow: gridRequest.endRow,
            fromBin: criteria.fromBin,
            toBin: criteria.toBin,
        };

        ReportApi.getCrossBinTransferDetail(request)
            .then((res) => {
                const result = res.data;

                // Normalize date for grid display
                result?.rows?.forEach((x: CrossBinTransferDetailModel) => {
                    x.dateActivated = x.dateActivated
                        ? new Date(x.dateActivated).toLocaleDateString()
                        : x.dateActivated;
                });

                const rows = result?.rows ?? [];
                const totalCount = result?.totalCount ?? rows.length;

                params?.success({
                    rowData: rows,
                    rowCount: totalCount,
                });

                if (rows.length > 0) {
                    params?.api?.hideOverlay();
                } else {
                    params?.api?.showNoRowsOverlay();
                }

                //Add pinned bottom totals row if summary detail provided
                if (result.summaryDetail?.totalAmount) {
                    const currencyCode = result.rows?.[0]?.currencyNumericCode;
                    const summaryRows = result.summaryDetail.totalAmount?.map((item) => ({
                        fromBin: "Totals",
                        transferAmount: item.totalTransferAmount,
                        currencyNumericCode: currencyCode
                    }));
                
                    (params?.api as any).setGridOption('pinnedBottomRowData', summaryRows)
                } else {
                    (params?.api as any).setGridOption('pinnedBottomRowData', []);
                }
            })
            .catch(() => {
                params?.fail?.();
            });
    };

    return (
        <>
            <div className="report-container">
                <div className="report-header">
                    <h5 className="mb-0">Cross-Bin Transfer Summary Report</h5>
                </div>
                <div className="blocks">

                    <SearchCriteriaRedReports
                        handleViewReport={handleViewReport}
                        handleDownloadReport={handleDownloadReport}
                        showToDate={true}
                    />
                </div>

                {isDownloadLoading && <LoadingComponent />}

                {hasRunReport && isSummaryLoading && <LoadingComponent />}

                {hasRunReport && (
                    <BaseGrid
                        colDefs={colDefs}
                        defaultColDef={defaultColDef}
                        getData={getReportData}
                        isGridReady={gridReady}
                        reportInputs={reportInputs}
                        dataSource={dataSource}
                        gridApi={gridApi}
                        setGridApi={setGridApi}
                        getContextMenuItems={getContextMenuItems}
                        autoSizeStrategy={autoSizeStrategy}
                    />
                )}

                { detailCriteria && dataSourceDetails && (
                    <BaseGrid
                        colDefs={colDefsDetails}
                        defaultColDef={defaultColDef}
                        getData={(params) => getDetailsReportData(params, detailCriteria)}
                        isGridReady={true}
                        reportInputs={reportInputs}
                        dataSource={dataSourceDetails}
                        gridApi={detailsGridApi}
                        setGridApi={setDetailsGridApi}
                        getContextMenuItems={getContextMenuItems}
                        autoSizeStrategy={autoSizeStrategy}
                    />
                )} 
            </div>
        </>
    );


};

CrossBinTransferSummary.displayName = "CrossBinTransfer";

export default CrossBinTransferSummary;
